import React, { useState, useRef, useEffect } from 'react';
import { 
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaTimes,
  FaSortAmountDown,
  FaSortAmountUp,
  FaUndo,
  FaEyeSlash,
  FaEye
} from 'react-icons/fa';
import styles from '../CSS/TicketFilters.module.css';

const TicketFilters = ({ 
  searchQuery, 
  setSearchQuery,
  filterCriteria, 
  setFilterCriteria,
  sortOption = 'created_at_desc',
  setSortOption = () => {},
  departamentos = [],
  adminsDepartamento = [],
  userRole = "admin", // "admin", "admin_departamento" o "user"
  showClosed = false, // NUEVO: para controlar si mostrar tickets cerrados
  setShowClosed = () => {} // NUEVO: función para cambiar estado
}) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // ✅ NUEVO: Estado para controlar la animación
  const filtersRef = useRef(null);

  // ✅ NUEVO: useEffect para activar la animación después del montaje
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100); // Pequeño delay para que la animación sea visible

    return () => clearTimeout(timer);
  }, []);

  // Cerrar filtros al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowAdvancedFilters(false);
      }
    };

    if (showAdvancedFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdvancedFilters]);

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchQuery.trim() !== '') count++;
    if (filterCriteria.estado !== 'todos') count++;
    if (filterCriteria.departamento !== 'todos') count++;
    if ((userRole === 'admin' || userRole === 'admin_departamento') && filterCriteria.asignado !== 'todos') count++;
    // NO contar showClosed como filtro activo
    return count;
  };

  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || 
           filterCriteria.estado !== 'todos' || 
           filterCriteria.departamento !== 'todos' || 
           ((userRole === 'admin' || userRole === 'admin_departamento') && filterCriteria.asignado !== 'todos');
    // NO incluir showClosed en filtros activos
  };

  // NUEVA FUNCIÓN: Manejar cambio de estado que sincroniza con showClosed
  const handleEstadoChange = (nuevoEstado) => {
    setFilterCriteria({...filterCriteria, estado: nuevoEstado});
    
    // Si selecciona "cerrado", activar showClosed automáticamente
    if (nuevoEstado === 'cerrado') {
      setShowClosed(true);
    }
    // Si quita el filtro de cerrado (selecciona "todos" u otro estado), desactivar showClosed
    else if (nuevoEstado !== 'cerrado' && showClosed) {
      setShowClosed(false);
    }
  };

  // NUEVA FUNCIÓN: Toggle de ordenamiento directo
  const toggleSort = () => {
    const newSort = sortOption === 'created_at_desc' ? 'created_at_asc' : 'created_at_desc';
    setSortOption(newSort);
  };

  return (
    <div className={`${styles.filtersWrapper} ${isVisible ? styles.fadeIn : styles.fadeOut}`}>
      <div 
        ref={filtersRef}
        className={`${styles.filtersContainer} ${hasActiveFilters() ? styles.hasActiveFilters : ''}`}
      >
        {/* Barra principal compacta */}
        <div className={styles.mainFilterBar}>
          {/* Búsqueda expandida */}
          <div className={styles.searchContainer}>
            <FaSearch className={styles.searchIcon} />
            <input 
              type="text"
              className={styles.searchInput}
              placeholder={
                (userRole === 'admin' || userRole === 'admin_departamento') 
                  ? "Buscar tickets por título o descripción..." 
                  : "Buscar en mis tickets..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                className={styles.clearSearchButton}
                onClick={() => setSearchQuery('')}
                type="button"
                aria-label="Limpiar búsqueda"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {/* Controles de filtros */}
          <div className={styles.filterControls}>
            {/* Toggle para mostrar/ocultar cerrados */}
            <button 
              className={`${styles.toggleClosedButton} ${showClosed ? styles.active : ''}`}
              onClick={() => setShowClosed(!showClosed)}
              type="button"
              aria-label={showClosed ? "Ocultar cerrados" : "Mostrar cerrados"}
              title={showClosed ? "Ocultar tickets cerrados" : "Mostrar tickets cerrados"}
            >
              {showClosed ? <FaEye /> : <FaEyeSlash />}
              <span className={styles.toggleText}>Cerrados</span>
            </button>

            {/* Ordenamiento SIMPLIFICADO - Solo botón toggle */}
            <button 
              className={styles.sortToggleButton}
              onClick={toggleSort}
              type="button"
              aria-label={sortOption === 'created_at_desc' ? "Ordenar más antiguos primero" : "Ordenar más recientes primero"}
              title={sortOption === 'created_at_desc' ? "Más recientes primero" : "Más antiguos primero"}
            >
              {sortOption === 'created_at_desc' ? <FaSortAmountDown /> : <FaSortAmountUp />}
            </button>

            {/* Botón de filtros */}
            <button 
              className={`${styles.filterToggleButton} ${showAdvancedFilters ? styles.active : ''}`}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              type="button"
              aria-label="Mostrar filtros"
              aria-expanded={showAdvancedFilters}
            >
              <FaFilter />
              <span className={styles.filterText}>Filtros</span>
              {getActiveFiltersCount() > 0 && (
                <span className={styles.filterBadge}>{getActiveFiltersCount()}</span>
              )}
              <FaChevronDown className={`${styles.chevron} ${showAdvancedFilters ? styles.rotated : ''}`} />
            </button>
          </div>
        </div>

        {/* Filtros avanzados flotantes */}
        {showAdvancedFilters && (
          <div className={styles.advancedFiltersOverlay}>
            <div className={styles.advancedFilters}>
              <div className={styles.filterGrid}>
                {/* Filtro por estado */}
                <div className={styles.filterItem}>
                  <div className={styles.filterLabelContainer}>
                    <label className={styles.filterLabel}>Estado</label>
                    {filterCriteria.estado !== 'todos' && (
                      <button
                        type="button"
                        className={styles.resetFilterButton}
                        onClick={() => setFilterCriteria({...filterCriteria, estado: 'todos'})}
                        aria-label="Resetear filtro de estado"
                      >
                        <FaUndo />
                      </button>
                    )}
                  </div>
                  <div className={styles.selectWrapper}>
                    <select 
                      value={filterCriteria.estado}
                      onChange={(e) => handleEstadoChange(e.target.value)}
                      className={styles.filterSelect}
                    >
                      <option value="todos">Por defecto</option>
                      <option value="nuevo">Nuevo</option>
                      <option value="en_proceso">En Proceso</option>
                      <option value="escalado">Escalado</option>
                      <option value="cerrado">Cerrado</option>
                    </select>
                  </div>
                </div>
                
                {/* Filtro por departamento */}
                <div className={styles.filterItem}>
                  <div className={styles.filterLabelContainer}>
                    <label className={styles.filterLabel}>Departamento</label>
                    {filterCriteria.departamento !== 'todos' && (
                      <button
                        type="button"
                        className={styles.resetFilterButton}
                        onClick={() => setFilterCriteria({...filterCriteria, departamento: 'todos'})}
                        aria-label="Resetear filtro de departamento"
                      >
                        <FaUndo />
                      </button>
                    )}
                  </div>
                  <div className={styles.selectWrapper}>
                    <select 
                      value={filterCriteria.departamento}
                      onChange={(e) => setFilterCriteria({...filterCriteria, departamento: e.target.value})}
                      className={styles.filterSelect}
                    >
                      <option value="todos">Todos los departamentos</option>
                      {departamentos.map(dep => (
                        <option key={dep.id} value={dep.id}>{dep.nombre}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Filtro por asignación (para admin y admin_departamento) */}
                {(userRole === 'admin' || userRole === 'admin_departamento') && (
                  <div className={styles.filterItem}>
                    <div className={styles.filterLabelContainer}>
                      <label className={styles.filterLabel}>Asignado a</label>
                      {filterCriteria.asignado !== 'todos' && (
                        <button
                          type="button"
                          className={styles.resetFilterButton}
                          onClick={() => setFilterCriteria({...filterCriteria, asignado: 'todos'})}
                          aria-label="Resetear filtro de asignación"
                        >
                          <FaUndo />
                        </button>
                      )}
                    </div>
                    <div className={styles.selectWrapper}>
                      <select 
                        value={filterCriteria.asignado}
                        onChange={(e) => setFilterCriteria({...filterCriteria, asignado: e.target.value})}
                        className={styles.filterSelect}
                      >
                        <option value="todos">Todos</option>
                        <option value="sin_asignar">Sin asignar</option>
                        {adminsDepartamento.map(admin => (
                          <option key={admin.id} value={admin.id}>{admin.nombre}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketFilters;